// eslint-disable-next-line max-classes-per-file
import { Asset, Catalog, Id, id as _id, ModuleCategory } from '@ess/jg-rule-executor';
import { ToJsonInputTranslatable } from '@src/app/model/to-json-input-translatable';
import {
  CatalogCategoryInput,
  CatalogInput,
  ModuleCategoryFilterSettingGqlInput,
  StringAttributeFilterSettingGqlInputTuple2,
  StringModuleCategoryFilterSettingGqlInputTuple2,
  StringOptionAssetInputList,
  StringOptionAssetInputTuple2,
} from '@src/app/generated/globalTypes';
import { PortalFilterSettings } from '@src/app/model/portal-filter-settings';
import {
  GetCatalog_catalogSetting,
  GetCatalog_catalogSetting_categories,
  GetCatalog_catalogSetting_filterSettings_values_value,
  GetCatalog_catalogSetting_filterSettings_values_value_attributes,
  GetCatalog_catalogSetting_filterSettings_values_value_attributes_values_value,
  GetCatalog_catalogSetting_filterSettings_values_value_attributes_values_value_valueIcons,
} from '@src/app/generated/GetCatalog';
import { PortalModuleCategory } from '@src/app/model/portal-module-category';

export class PortalCatalog implements ToJsonInputTranslatable {
  id: Id<Catalog>;
  categories: PortalCatalogCategory[];
  filterSettings: PortalFilterSettings;

  constructor(id?: Id<Catalog>, categories?: PortalCatalogCategory[], filterSettings?: Map<string, SettingValue>) {
    this.categories = categories;
    this.filterSettings = new PortalFilterSettings(filterSettings);
  }

  /**
   * a default catalog to use as initial value
   */
  static default(): PortalCatalog {
    return new PortalCatalog(
      undefined,
      [new PortalCatalogCategory(
        'CATALOG_CATEGORY_TITLE',
        [_id('MODULE_CATEGORY_ID')]
      )],
      new Map([[
        'MOD_CAT_ID',
        new SettingValue(
          new SettingAttributes(
            new Map([[
              'ATTR_ID',
              new SettingAttributeValue(
                new ValueIcon(
                  new Map([[
                    'value',
                    new Asset(_id('fileId'), 'dir', 'fileName', 'storageFileName', 'png'),
                  ]])
                )
              ),
            ]])
          )
        ),
      ]])
    );
  }

  static fromGql(gql: GetCatalog_catalogSetting): PortalCatalog {
    const filterSettingsMap = new Map<string, SettingValue>();
    gql.filterSettings?.values?.map((g) => {
      filterSettingsMap.set(
        g.moduleCategoryReference,
        SettingValue.fromGql(g.value)
      );
    });
    return new PortalCatalog(
      gql.id,
      gql.categories?.map(PortalCatalogCategory.fromGql),
      filterSettingsMap
    );
  }

  public toGqlInput(): CatalogInput {
    const settingValueInput = [];
    this.filterSettings?.forEach((v, k) => {
      settingValueInput.push(SettingValue.toGqlInputFromMap(k, v));
    });
    return {
      categories: this.categories?.map(PortalCatalogCategory.categoriesToGqlInput) ?? [],
      filterSettings: {
        values: settingValueInput,
      },
    };
  }
}

/**
 * This class is a workaround to show the categoryReferences in the json editor, instead of the ids.
 */
export class JsonCatalog implements ToJsonInputTranslatable {
  id: Id<Catalog>;
  categories: JsonCatalogCategory[];
  filterSettings: { values: any[] };

  moduleCategories: PortalModuleCategory[];

  constructor(catalog: PortalCatalog, moduleCategories: PortalModuleCategory[]) {
    this.id = catalog.id;
    this.moduleCategories = moduleCategories;
    this.categories = catalog.categories.map(cat => new JsonCatalogCategory(
      cat.title,
      cat.moduleCategoryIds.map(catmc => this.moduleCategories.find(mc => mc.id === catmc)?.reference)
    ));
    const settingValueInput = [];
    catalog.filterSettings?.forEach((v, k) => {
      settingValueInput.push(SettingValue.toGqlInputFromMap(k, v));
    });
    this.filterSettings = {
      values: settingValueInput,
    };
  }

  toGqlInput(): object {
    return this;
  }

  static toJsonInput(catalogInput: CatalogInput): JSON {
    return { catalog: catalogInput } as object as JSON;
  }

  /**
   * This function has to be static, because we pass a json object which we up-cast to JsonCatalog
   * @param json
   */
  public static toCatalogInput(json: JsonCatalog): CatalogInput {
    return {
      categories: json.categories.map(cat => new PortalCatalogCategory(
        cat.title,
        cat.moduleCategoryReferences.map(catmc => json.moduleCategories.find(mc => mc.reference === catmc)?.id)
      )).map(PortalCatalogCategory.categoriesToGqlInput) ?? [],
      filterSettings: json.filterSettings,
    };
  }
}

export class PortalCatalogCategory {
  title: string;
  moduleCategoryIds: Id<ModuleCategory>[];

  constructor(title: string, moduleCategoryIds: Id<ModuleCategory>[]) {
    this.title = title;
    this.moduleCategoryIds = moduleCategoryIds;
  }

  static fromGql(gql: GetCatalog_catalogSetting_categories): PortalCatalogCategory {
    return new PortalCatalogCategory(
      gql.title,
      gql.moduleCategoryIds
    );
  }

  static categoriesToGqlInput(category: PortalCatalogCategory): CatalogCategoryInput {
    return {
      title: category.title,
      moduleCategoryIds: category.moduleCategoryIds,
    };
  }
}

export class JsonCatalogCategory {
  title: string;
  moduleCategoryReferences: string[];

  constructor(title: string, moduleCategoryReferences: string[]) {
    this.title = title;
    this.moduleCategoryReferences = moduleCategoryReferences;
  }
}

export class SettingValue {
  attributes: SettingAttributes;

  constructor(attributes: SettingAttributes) {
    this.attributes = attributes;
  }

  static fromGql(gql: GetCatalog_catalogSetting_filterSettings_values_value): SettingValue {
    return new SettingValue(SettingAttributes.fromGql(gql.attributes));
  }

  static toGqlInput(key: string, value: SettingValue): StringModuleCategoryFilterSettingGqlInputTuple2 {
    return {
      key,
      value: SettingAttributes.toGqlInput(value.attributes),
    };
  }

  static toGqlInputFromMap(
    key: string,
    value: Map<string, Map<string, Asset>>
  ): StringModuleCategoryFilterSettingGqlInputTuple2 {
    return {
      key,
      value: SettingAttributes.toGqlInputFromMap(value),
    };
  }
}

export class SettingAttributes {
  values: Map<string, SettingAttributeValue>;

  constructor(values: Map<string, SettingAttributeValue>) {
    this.values = values;
  }

  static fromGql(gql: GetCatalog_catalogSetting_filterSettings_values_value_attributes): SettingAttributes {
    const map = new Map<string, SettingAttributeValue>();
    gql.values.map((g) => {
      map.set(
        g.attributeId,
        SettingAttributeValue.fromGql(g.value)
      );
    });
    return new SettingAttributes(map);
  }

  static toGqlInput(attributes: SettingAttributes): ModuleCategoryFilterSettingGqlInput {
    const attributeValuesInput = [];
    attributes.values.forEach((v, k) => {
      attributeValuesInput.push(SettingAttributeValue.toGqlInput(k, v));
    });
    return { attributes: { values: attributeValuesInput } };
  }

  static toGqlInputFromMap(attributes: Map<string, Map<string, Asset>>): ModuleCategoryFilterSettingGqlInput {
    const attributeValuesInput = [];
    attributes.forEach((v, k) => {
      attributeValuesInput.push(SettingAttributeValue.toGqlInputFromMap(k, v));
    });
    return { attributes: { values: attributeValuesInput } };
  }
}

export class SettingAttributeValue {
  valueIcons: ValueIcon;

  constructor(valuesIcons: ValueIcon) {
    this.valueIcons = valuesIcons;
  }

  static fromGql(
    gql: GetCatalog_catalogSetting_filterSettings_values_value_attributes_values_value
  ): SettingAttributeValue {
    return new SettingAttributeValue(ValueIcon.fromGql(gql.valueIcons));
  }

  static toGqlInput(key: string, value: SettingAttributeValue): StringAttributeFilterSettingGqlInputTuple2 {
    return {
      key,
      value: {
        valueIcons: ValueIcon.toGqlInput(value.valueIcons),
      },
    };
  }

  static toGqlInputFromMap(key: string, value: Map<string, Asset>): StringAttributeFilterSettingGqlInputTuple2 {
    return {
      key,
      value: {
        valueIcons: ValueIcon.toGqlInputFromMap(value),
      },
    };
  }
}

export class ValueIcon {
  values: Map<string, Asset>;

  constructor(values: Map<string, Asset>) {
    this.values = values;
  }

  static fromGql(
    gql: GetCatalog_catalogSetting_filterSettings_values_value_attributes_values_value_valueIcons
  ): ValueIcon {
    const map = new Map<string, Asset>();
    gql.values.map((g) => {
      map.set(g.attributeValue, g.iconAsset
        ? new Asset(
          g.iconAsset.id,
          g.iconAsset.filename,
          g.iconAsset.directory,
          g.iconAsset.mimeType,
          g.iconAsset.url
        )
        : undefined);
    });
    return new ValueIcon(map);
  }

  static toGqlInput(icon: ValueIcon): StringOptionAssetInputList {
    const valueIconsInput = [];
    icon.values.forEach((v, k) => {
      valueIconsInput.push(IconValue.toGqlInput(k, v));
    });
    return { values: valueIconsInput };
  }

  static toGqlInputFromMap(icon: Map<string, Asset>): StringOptionAssetInputList {
    const valueIconsInput = [];
    icon.forEach((v, k) => {
      valueIconsInput.push(IconValue.toGqlInput(k, v));
    });
    return { values: valueIconsInput };
  }
}

export class IconValue extends Asset {
  static toGqlInput(key: string, value: Asset): StringOptionAssetInputTuple2 {
    return {
      key,
      value: value
        ? {
          id: value.id,
          mimeType: value.mimeType,
          directory: value.directory,
          filename: value.filename,
        }
        : null,
    };
  }
}
